import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  ViewChild,
} from "@angular/core";
import { UserService } from "../users/users.service";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { Group } from "../users/users.interface";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-group",
  templateUrl: "./group.component.html",
  styleUrls: ["./group.component.scss"],
  standalone: false,
})
export class GroupComponent implements OnInit, OnChanges {
  displayedColumns: string[] = [];
  dataSource: MatTableDataSource<Group>;
  expandedGroupId: string | null = null;
  members: string[] = [];
  @Input() updating? = false;
  @Input() currentSignatureId: string;
  @Input() signatureName!: string;
  @Input() showAliasOptions = true;
  @Output() clicked = new EventEmitter<string>();
  @Output() uninstall = new EventEmitter<string>();
  @Output() clickedAll = new EventEmitter<{ group: string; alias: string[] }>();

  @ViewChild(MatSort) sort: MatSort;

  checkedDefaultInstallationGroups = new Set<string>();
  isLoading = false;
  isDataLoaded = false;

  constructor(
    private userService: UserService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.dataSource = new MatTableDataSource<Group>([]);
    this.updateDisplayedColumns();
  }

  ngOnChanges() {
    this.updateDisplayedColumns();
  }

  private updateDisplayedColumns() {
    this.displayedColumns = ["expand", "name", "email", "directMembersCount"];

    if (this.showAliasOptions) {
      this.displayedColumns.push("defaultForEmployees");
    }

    this.displayedColumns.push("assign");
  }

  loadData(force = false) {
    if (this.isDataLoaded && !force) {
      return;
    }

    this.isLoading = true;
    this.fetchGroups();
  }

  private fetchGroups() {
    this.userService.groups().subscribe((groups) => {
      this.dataSource.data = groups;
      this.dataSource.sort = this.sort;
      this.isLoading = false;
      this.isDataLoaded = true;
      this.checkedDefaultInstallationGroups = new Set(
        groups
          .filter((group) => group.templateId === this.currentSignatureId)
          .map((group) => this.groupKey(group)),
      );
    });
  }

  public isDefaultGroupInstallationSelected(signatureId: string): boolean {
    return this.checkedDefaultInstallationGroups.has(signatureId);
  }

  assign(group: string) {
    return this.clicked.emit(group);
  }

  assignToAll(group: string, alias: string) {
    return this.clickedAll.emit({ group, alias: [alias] });
  }

  toggleMembers(groupId: string) {
    if (this.expandedGroupId === groupId) {
      this.expandedGroupId = null;
      this.members = [];
    } else {
      this.expandedGroupId = groupId;
      const selectedGroup = this.dataSource.data.find(
        (group) => this.groupKey(group) === groupId,
      );
      this.members = selectedGroup ? selectedGroup.members : [];
    }
  }

  toggleDefaultInstallation(event: MatSlideToggleChange, group: Group) {
    event.source.checked = !event.source.checked; // prevent toggle, as we will do it manually after user confirmation
    const params = {
      template: this.signatureName,
      group: group.name,
    };
    const key = this.groupKey(group);

    if (this.checkedDefaultInstallationGroups.has(key)) {
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: this.translate.instant(
              "signature-group.dialog.removeDescription",
              params,
            ) as string,
          },
        })
        .afterClosed()
        .subscribe((confirm) => {
          if (confirm) {
            this.checkedDefaultInstallationGroups.delete(key);
            event.source.checked = false;
            this.toggleInstallationAfterUserConfirmation(group, false);
          }
        });
      return;
    }

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "signature-group.dialog.description",
            params,
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.checkedDefaultInstallationGroups.add(key);
          event.source.checked = true;
          this.toggleInstallationAfterUserConfirmation(group, true);
        }
      });
  }

  groupKey(group: Group): string {
    return group.organisationGroupId;
  }

  private toggleInstallationAfterUserConfirmation(
    group: Group,
    installed: boolean,
  ) {
    this.userService
      .toggleDefaultInstallationForGroup(
        group.email,
        this.currentSignatureId,
        installed,
      )
      .subscribe(() => {
        if (installed) {
          this.clicked.emit(group.email);
        } else {
          this.uninstall.emit(group.email);
        }
        this.fetchGroups();
      });
  }
}
